/**
 * V60.x: useTransferRequests Hook (BE callable 기반)
 *
 * listTransferRequests CF를 호출하여 전배 요청 목록을 조회합니다.
 * 서버에서 getCompanyFilterByUserId()로 회사 스코프를 확정하므로
 * FE에서 companyCode를 전달하지 않습니다.
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { ApprovalRequest } from '../types/approvalRequest';

/**
 * 전배 승인 요청 필터 (BE callable용)
 */
export interface TransferApprovalListFilter {
  /** approvalRequests.status 필터 */
  status?: string | string[];
  /** 요청자 ID (서버에서 추가 필터링) */
  requesterId?: string;
  /** 받는 조직 ID */
  toOrgUnitId?: string;
  /** 승인자 ID (서버에서 추가 필터링) */
  approverId?: string;
}

/**
 * Hook 반환 타입
 */
export interface UseTransferApprovalRequestsResult {
  transferRequests: ApprovalRequest[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * 전배 승인 요청 목록 조회 Hook (BE callable 기반)
 *
 * listTransferRequests CF를 호출합니다.
 * 회사 스코프는 서버에서 결정합니다 (system_admin=ALL, role_admin_kr=KR, etc.)
 */
export function useTransferRequests(
  filters: TransferApprovalListFilter = {}
): UseTransferApprovalRequestsResult {
  const [transferRequests, setTransferRequests] = useState<ApprovalRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0);

  useEffect(() => {
    const fetchTransferRequests = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!functions) {
          throw new Error('Firebase Functions 초기화 실패');
        }

        const fn = httpsCallable(functions, 'listTransferRequests');
        const result = await fn({
          status: filters.status,
          requesterId: filters.requesterId,
          toOrgUnitId: filters.toOrgUnitId,
          approverId: filters.approverId,
        });

        const data = result.data as any;

        if (!data.success) {
          throw new Error(data.message || '전배 목록 조회 실패');
        }

        const requests: ApprovalRequest[] = (data.items || []).map((item: any) => ({
          id: item.id,
          type: item.type,
          requesterId: item.requesterId || '',
          requesterName: item.requesterName || '',
          requesterOrgUnitId: item.requesterOrgUnitId || '',
          requesterOrgUnitName: item.requesterOrgUnitName || '',
          approverId: item.approverId || '',
          approverName: item.approverName || '',
          status: item.status,
          targetDocumentId: item.targetDocumentId || '',
          targetCollection: item.targetCollection || '',
          requestData: item.requestData || {},
          comment: item.comment,
          approvedAt: item.approvedAt,
          rejectedAt: item.rejectedAt,
          createdAt: item.createdAt,
          orgApprovedAt: item.orgApprovedAt,
          orgApprovedBy: item.orgApprovedBy,
          hrApprovedAt: item.hrApprovedAt,
          hrApprovedBy: item.hrApprovedBy,
          hrApproverName: item.hrApproverName,
          companyCode: item.companyCode,
          // transfer 전용 필드
          transferMode: item.transferMode,
          transferEffectiveDate: item.transferEffectiveDate,
          transferTargetUserId: item.transferTargetUserId,
          transferFromOrgUnitId: item.transferFromOrgUnitId,
          transferToOrgUnitId: item.transferToOrgUnitId,
          appliedAt: item.appliedAt,
        } as ApprovalRequest));

        setTransferRequests(requests);
        setIsLoading(false);
      } catch (err) {
        console.error('전배 승인 요청 목록 조회 실패:', err);
        setError(err as Error);
        setIsLoading(false);
      }
    };

    fetchTransferRequests();
  }, [filters.status?.toString(), filters.requesterId, filters.toOrgUnitId, filters.approverId, refetchTrigger]);

  const refetch = () => {
    setRefetchTrigger((prev) => prev + 1);
  };

  return { transferRequests, isLoading, error, refetch };
}

/**
 * 전배 상태별 한국어 라벨
 */
export const TRANSFER_STATUS_LABELS: Record<string, string> = {
  pending: '승인대기',
  orgApproved: '인사팀 대기',
  approved_scheduled: '예약대기',
  applied: '완료',
  rejected: '반려',
};

/**
 * 전배 상태별 색상
 */
export const TRANSFER_STATUS_COLORS: Record<string, string> = {
  pending: 'blue',
  orgApproved: 'purple',
  approved_scheduled: 'orange',
  applied: 'green',
  rejected: 'red',
};
